import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const FLAG_URL = (cc) => `https://flagcdn.com/20x15/${cc.toLowerCase()}.png`;

const PROTOCOL_STYLE = {
  http:    'text-blue-500 bg-blue-50 border-blue-200',
  https:   'text-cyan-500 bg-cyan-50 border-cyan-200',
  socks4:  'text-purple-500 bg-purple-50 border-purple-200',
  socks5:  'text-violet-500 bg-violet-50 border-violet-200',
  socks5h: 'text-violet-500 bg-violet-50 border-violet-200',
};

const ANON_STYLE = {
  Transparent: 'text-red-400 bg-red-50 border-red-200',
  Anonymous:   'text-amber-500 bg-amber-50 border-amber-200',
  Elite:       'text-emerald-500 bg-emerald-50 border-emerald-200',
};

function StatusBadge({ result }) {
  if (!result) return (
    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold border text-slate-400 bg-slate-50 border-slate-200">⏳ Wait</span>
  );
  if (result.checking) return (
    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold border text-blue-500 bg-blue-50 border-blue-200 whitespace-nowrap">
      <span className="animate-spin inline-block">🔄</span> Check
    </span>
  );
  if (result.ip && !result.error) return (
    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold border text-emerald-600 bg-emerald-50 border-emerald-200">✅ Alive</span>
  );
  return (
    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold border text-red-500 bg-red-50 border-red-200">❌ Dead</span>
  );
}

function ProtocolBadge({ protocol }) {
  if (!protocol) return <span className="text-slate-300">—</span>;
  const key = protocol.toLowerCase();
  const cls = PROTOCOL_STYLE[key] || 'text-slate-500 bg-slate-50 border-slate-200';
  return <span className={`inline-flex px-2 py-1 rounded-md border text-[10px] font-bold uppercase tracking-wider ${cls}`}>{protocol}</span>;
}

function AnonBadge({ anonymity }) {
  if (!anonymity) return <span className="text-slate-300">—</span>;
  const cls = ANON_STYLE[anonymity] || 'text-slate-500 bg-slate-50 border-slate-200';
  return <span className={`inline-flex px-2 py-1 rounded-md border text-[10px] font-bold ${cls}`}>{anonymity}</span>;
}

function LocationCell({ result }) {
  if (!result?.location) return <span className="text-slate-300">—</span>;
  const { country_code } = result.location;
  const { city, region, postal, timezone, loc } = result;
  const mainLoc = [city, region, country_code].filter(Boolean).join(', ');
  const tzDisplay = timezone ? timezone.split('/').pop().replace(/_/g, ' ') : null;
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-2 flex-wrap">
        {country_code && (
          <img src={FLAG_URL(country_code)} alt={country_code} className="rounded-[3px] shadow-sm flex-shrink-0" width={18} height={14} onError={(e) => { e.target.style.display = 'none'; }} />
        )}
        <span className="text-slate-700 font-medium text-[13px]">{mainLoc || 'Unknown'}</span>
        {postal && <span className="text-slate-400 text-[10px] font-mono bg-slate-100 px-1.5 py-0.5 rounded border border-slate-200">{postal}</span>}
      </div>
      {(tzDisplay || loc) && (
        <div className="flex items-center gap-2 flex-wrap">
          {tzDisplay && <span className="flex items-center gap-1 text-[11px] text-blue-500/90 bg-blue-50 px-1.5 py-0.5 rounded-md font-medium border border-blue-100/50">🕒 {tzDisplay}</span>}
          {loc && <span className="flex items-center gap-1 text-[11px] text-emerald-600/90 bg-emerald-50 px-1.5 py-0.5 rounded-md font-medium border border-emerald-100/50">📍 {loc}</span>}
        </div>
      )}
    </div>
  );
}

function OrgCell({ org }) {
  if (!org) return <span className="text-slate-300">—</span>;
  const clean = org.replace(/^AS\d+\s+/i, '');
  return <div className="text-slate-600 text-[13px] max-w-[200px] line-clamp-2" title={org}>{clean}</div>;
}

function SpeedCell({ total_time }) {
  if (total_time == null) return <span className="text-slate-300">—</span>;
  const ms = Math.round(total_time * 1000);
  const cls = ms < 500 ? 'text-emerald-500' : ms < 1500 ? 'text-amber-500' : 'text-red-400';
  return <span className={`font-mono text-[13px] font-bold ${cls}`}>{ms} ms</span>;
}

export default function ResultsTable({ proxyList, resultsMap }) {
  return (
    <div className="bg-white border border-orange-100 rounded-2xl shadow-sm overflow-hidden flex flex-col">
      <div className="p-4 border-b border-orange-50 bg-gradient-to-r from-orange-50 to-pink-50 flex items-center justify-between">
        <h3 className="text-slate-700 font-bold flex items-center gap-2 text-sm">
          <span className="text-base">🐾</span> Detailed Results
        </h3>
        <span className="text-xs text-slate-400 font-medium">{proxyList.length} proxies</span>
      </div>

      <div className="overflow-auto custom-scrollbar" style={{ maxHeight: '60vh' }}>
        <table className="w-full text-left text-sm text-slate-500 min-w-[1100px]">
          <thead className="bg-orange-50/90 text-[11px] uppercase font-bold text-slate-400 sticky top-0 z-10 backdrop-blur-md shadow-[0_1px_2px_rgba(0,0,0,0.02)]">
            <tr>
              <th className="px-5 py-3.5 tracking-wider whitespace-nowrap">Proxy</th>
              <th className="px-4 py-3.5 tracking-wider whitespace-nowrap">Status</th>
              <th className="px-4 py-3.5 tracking-wider whitespace-nowrap">Protocol</th>
              <th className="px-4 py-3.5 tracking-wider whitespace-nowrap">Anonymity</th>
              <th className="px-4 py-3.5 tracking-wider whitespace-nowrap">IP</th>
              <th className="px-4 py-3.5 tracking-wider whitespace-nowrap">Location</th>
              <th className="px-4 py-3.5 tracking-wider whitespace-nowrap">ISP / Org</th>
              <th className="px-5 py-3.5 tracking-wider whitespace-nowrap text-right">Speed</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-orange-50/60">
            <AnimatePresence>
              {proxyList.map((dsn, index) => {
                const result = resultsMap.get(index);
                return (
                  <motion.tr
                    key={`${index}-${dsn}`}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2, delay: Math.min(index * 0.02, 0.3) }}
                    className="hover:bg-orange-50/40 transition-colors group"
                  >
                    <td className="px-5 py-4 font-mono text-slate-500 group-hover:text-slate-800 transition-colors text-[12px] whitespace-nowrap">{dsn}</td>
                    <td className="px-4 py-4 whitespace-nowrap"><StatusBadge result={result} /></td>
                    <td className="px-4 py-4 whitespace-nowrap"><ProtocolBadge protocol={result?.protocol} /></td>
                    <td className="px-4 py-4 whitespace-nowrap"><AnonBadge anonymity={result?.anonymity} /></td>
                    <td className="px-4 py-4 font-mono text-[12px] text-slate-500 whitespace-nowrap">{result?.ip || <span className="text-slate-300">—</span>}</td>
                    <td className="px-4 py-4 min-w-[220px]"><LocationCell result={result} /></td>
                    <td className="px-4 py-4"><OrgCell org={result?.org} /></td>
                    <td className="px-5 py-4 text-right whitespace-nowrap"><SpeedCell total_time={result?.total_time} /></td>
                  </motion.tr>
                );
              })}
            </AnimatePresence>

            {proxyList.length === 0 && (
              <tr>
                <td colSpan="8" className="px-5 py-24 text-center text-slate-400">
                  <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}>
                    <div className="text-6xl mb-4">🐱</div>
                    <p className="font-bold text-slate-500">No proxies yet, meow~</p>
                    <p className="text-xs mt-1 text-slate-400">Add proxies and start checking to see results here.</p>
                  </motion.div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}